import { z } from 'zod';
import { ITestCaseSchema } from './testCase';
import { IUserSchema } from './user';
import { useMutation, useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/utils/libs/axios';
import { ITestIssueSchema } from './testIssue';
import { ISelectOption } from '@/components/form/fields/SelectField';
import { Dialog } from 'alurkerja-ui';

export const detailTestRunSchema = z.object({
  id: z.string().optional(),
  test_run_id: z.string().optional(),
  test_case_id: z.string().optional(),
  assignee_id: z.string().optional(),
  status: z.string().optional(),
  test_run_detail_issue: z.array(z.object({
    id: z.string().optional(),
    test_run_detail_id: z.string(),
    test_issue_id: z.string(),
    is_create: z.boolean(),
  })).optional(),
});

export type IDetailTestRunSchema = z.infer<typeof detailTestRunSchema> & {
  assignee?: IUserSchema;
  test_case?: ITestCaseSchema;
  test_issue?: ITestIssueSchema;
};

export type IDetailTestRunOptions = {
  status: ISelectOption[];
  type: ISelectOption[];
};

export const TestRunDetailService = {
  useGet: (id?: string) =>
    useQuery<IDetailTestRunSchema>({
      queryKey: ['test-run-detail', id],
      queryFn: async () => {
        if (!id) return {};

        const response = await axiosInstance.get(
          `/api/crud/test-run-detail/${id}`,
        ).catch(error => {
          console.error('Error fetching test run detail:', error);

          Dialog.error({
            title: 'Error',
            description: 'Failed to get test run detail. Please try again later.',
            callback: () => {
              // hapus ?test-run-detail-id dari URL
              const url = new URL(window.location.href);
              url.searchParams.delete('test-run-detail-id');
              window.history.pushState({}, '', url.toString());
            }
          });
        }).finally(() => {
          // Optionally handle any cleanup or final actions here
          console.log('Fetch test run detail completed');
        });

        return response?.data?.data;
      },
    }),

  useGetOptions: () =>
    useQuery<IDetailTestRunOptions>({
      queryKey: ['test-run-detail-options'],
      queryFn: async () => {
        const { data: optionsData } = await axiosInstance.get(
          '/api/crud/test-run/options',
        );

        return {
          status: optionsData.data.status,
          type: optionsData.data.type,
        };
      },
    }),

  useUpdate: () =>
    useMutation({
      mutationFn: async (data: IDetailTestRunSchema) => {
        const response = await axiosInstance.post(
          `/api/crud/test-run-detail/${data.id}`,
          data,
        );

        return response.data;
      },
    }),

    useUpdateStatus: () =>
    useMutation({
      mutationFn: async ({
        id,
        status,
      }: {
        id: string;
        status: string;
      }) => {
        const response = await axiosInstance.post(
          `/api/crud/test-run-detail/${id}/status`,
          { status },
        );

        return response.data;
      },
    }),

  useDelete: () =>
    useMutation({
      mutationFn: async (data: IDetailTestRunSchema) => {
        await axiosInstance.delete(`/api/crud/test-run-detail/${data.id}`);
      },
    }),
};
